import { DndContext, closestCenter, DragEndEvent } from "@dnd-kit/core";
import { SortableContext, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { QuestionType } from "@simplyk/common";

import { getQuestionTypeIcon, getQuestionTypeSubtitle } from "./helpers/questionTypeIcons";
import { QuestionCard } from "./QuestionCard/QuestionCard";
import { QuestionScope } from "./types";

import { useTranslate } from "@/common/hooks/useTranslate";

// scope is optional: a sign-up form has one audience, so its rows carry none.
export interface SortableQuestion {
  localId: string;
  body: string;
  type: QuestionType;
  required: boolean;
  choices?: { body: string }[];
  scope?: QuestionScope;
}

interface SortableQuestionCardsProps<TQuestion extends SortableQuestion> {
  questions: TQuestion[];
  onReorder: (event: DragEndEvent) => void;
  onEdit: (question: TQuestion) => void;
  onDelete: (localId: string) => void;
}

export const SortableQuestionCards = <TQuestion extends SortableQuestion>({
  questions,
  onReorder,
  onEdit,
  onDelete,
}: SortableQuestionCardsProps<TQuestion>) => {
  const { t } = useTranslate();

  // An empty DndContext mounts hidden a11y nodes that break the parent Stack's first-child spacing
  if (questions.length === 0) {
    return null;
  }

  const isReorderable = questions.length > 1;

  return (
    <DndContext collisionDetection={closestCenter} onDragEnd={onReorder}>
      <SortableContext items={questions.map((question) => question.localId)} strategy={verticalListSortingStrategy}>
        {questions.map((question, index) => (
          <QuestionCard
            key={question.localId}
            icon={getQuestionTypeIcon(question.type)}
            label={question.body}
            subtitle={getQuestionTypeSubtitle(t, question.type, question.choices)}
            required={question.required}
            sortableId={isReorderable ? question.localId : undefined}
            onEdit={() => onEdit(question)}
            onDelete={() => onDelete(question.localId)}
            questionIndex={index}
            questionScope={question.scope}
          />
        ))}
      </SortableContext>
    </DndContext>
  );
};
